/**
 * フッターの Contact / Audition にスクロール連動の視差を付ける
 *
 * @description Contactは下へ、Auditionは上へずらして、2枚の箱がすれ違うように見せる。
 *              箱は上に流れるマーキーと重なるので、動かす量は箱が親の切り抜きに
 *              触らない範囲に収めている。
 */

import { gsap } from 'gsap/dist/gsap.min.js';
import { ScrollTrigger } from 'gsap/dist/ScrollTrigger.min.js';

gsap.registerPlugin(ScrollTrigger);

const DESIGN_WIDTH = 1728;

/**
 * 中央からのずれ幅（デザイン基準px）
 *
 * @description 箱の下端は741と902で、親の切り抜きは1155以上。下へ動かすほうに余裕があるので
 *              クリエイター一覧の視差と同じ量に揃えている
 */
const PARALLAX = 48;

/**
 * デザイン上のpx値を、現在のビューポート幅に応じたpx値へ変換する
 * SCSSのvw()と同じ計算なので、画面幅が変わってもずれ幅の比率が保たれる
 *
 * @param value デザイン幅1728px基準のpx値
 * @returns 現在の画面幅に換算したpx値
 */
const toVwPx = (value: number): number => (window.innerWidth * value) / DESIGN_WIDTH;

// 生きているトゥイーン。非同期遷移で古いDOMを捨てるとき、
// 外れた要素を見続けるScrollTriggerが残らないように持っておく
let tweens: gsap.core.Tween[] = [];

/**
 * 箱にスクロール連動の縦方向パララックスを付ける
 *
 * @param target 対象の箱
 * @param direction 1で下方向、-1で上方向
 */
const applyParallax = (target: HTMLElement, direction: number): void => {
    tweens.push(gsap.fromTo(
        target,
        { y: () => -toVwPx(PARALLAX) * direction },
        {
            y: () => toVwPx(PARALLAX) * direction,
            ease: 'none',
            scrollTrigger: {
                trigger: '.footer__lead',
                start: 'top bottom',
                end: 'bottom top',
                scrub: true,
                // ずれ幅が画面幅依存なのでリサイズ時に再計算させる
                invalidateOnRefresh: true,
            },
        },
    ));
};

/**
 * フッターの視差を破棄する
 */
const destroyFooterParallax = (): void => {
    tweens.forEach((tween) => {
        tween.scrollTrigger?.kill();
        tween.kill();
    });

    tweens = [];
};

/**
 * フッターの視差を始める
 */
const initFooterParallax = (): void => {
    destroyFooterParallax();

    // 完了ページや404はミニフッターなので、この箱そのものが無い
    const lead = document.querySelector<HTMLElement>('.footer__lead');
    if (!lead) return;

    // 視差は装飾なので、モーションを抑える設定では動かさない
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    const contact = lead.querySelector<HTMLElement>('.footer__leadBox.attr-contact');
    const audition = lead.querySelector<HTMLElement>('.footer__leadBox.attr-audition');

    if (contact) applyParallax(contact, 1);
    if (audition) applyParallax(audition, -1);
};

export { initFooterParallax, destroyFooterParallax };

document.addEventListener('DOMContentLoaded', initFooterParallax);
